import * as React from 'react';
import Icon, { LoadingOutlined, UploadOutlined, MenuOutlined } from '@ant-design/icons';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Icon as LegacyIcon } from '@ant-design/compatible';
import Bugsnag from '@shared/bugsnagClient';
import {
  BuildingsMultiple,
  Checkmark,
  ChevronLeftShadow,
  ChevronLeftSlate,
  ChevronRightShadow,
  ChevronRightSlate,
  ChevronLeft,
  ChevronRight,
  Close,
  CloseCircle,
  CompanyLogo,
  Desktop,
  Download,
  Download2,
  DownloadMultiple,
  DropdownDown,
  DropdownUp,
  Edit,
  Email,
  ExclamationCircle,
  Eye,
  Filters,
  FirstToMarketLabel,
  FlexibleSizeArea,
  FloorPlan,
  Heart,
  HeartOutline,
  Info,
  LangSelectionGBFlag,
  LangSelectionUSFlag,
  Leeds,
  Link,
  Location,
  Lock,
  MagnifyingGlass,
  MapRefresh,
  MediaFile,
  Minus,
  Overflow,
  PersonCircle,
  PinDrop,
  Plus,
  Share,
  ThreeDTour,
  Tourbook,
  TourbookFilled,
  TourbookWithNoListings,
  Trash,
  TruvaT,
  Union,
  Upload,
  VtsLogoBlack,
  VtsLogoIndigo,
  WiredScore,
  WiredScoreCertified,
  WiredScoreGold,
  WiredScorePlatinum,
  WiredScoreSilver,
  EmptyBuilding,
} from './icons';

/*
 * PLEASE HELP US KEEP THIS MANAGABLE:
 * 1) use descriptive names, no whatever1 and whatever2
 * 2) take the fills off the svg and color w/ CSS
 * do not make extra versions of the same icon in different colors
 * 3) name all antd icons clearly, i.e. antdLoadingOutlined
 * 4) try to keep this list in _some_ order
 */

// all icons in the below list are automatically in the design-system
export const componentMappings = {
  '3d-tour': { Component: ThreeDTour },
  antdLoadingOutlined: { Component: LoadingOutlined },
  antdUploadOutlined: { Component: UploadOutlined },
  antdMenuOutlined: { Component: MenuOutlined },
  'buildings-multiple': { Component: BuildingsMultiple },
  checkmark: { Component: Checkmark },
  'chevron-left': { Component: ChevronLeft },
  'chevron-up': { Component: DropdownUp },
  'chevron-down': { Component: DropdownDown },
  'chevron-left-shadow': { Component: ChevronLeftShadow },
  'chevron-left-slate': { Component: ChevronLeftSlate },
  'chevron-right': { Component: ChevronRight },
  'chevron-right-shadow': { Component: ChevronRightShadow },
  'chevron-right-slate': { Component: ChevronRightSlate },
  close: { Component: Close },
  'close-circle': { Component: CloseCircle },
  'company-logo': { Component: CompanyLogo },
  desktop: { Component: Desktop },
  download: { Component: Download },
  download2: { Component: Download2 },
  'download-multiple': { Component: DownloadMultiple },
  'dropdown-down': { Component: DropdownDown },
  'dropdown-up': { Component: DropdownUp },
  edit: { Component: Edit },
  email: { Component: Email },
  'exclamation-circle': { Component: ExclamationCircle },
  eye: { Component: Eye },
  filters: { Component: Filters },
  'first-to-market': { Component: FirstToMarketLabel },
  'flexible-size-area': { Component: FlexibleSizeArea },
  'floor-plan': { Component: FloorPlan },
  heart: { Component: Heart },
  'heart-outline': { Component: HeartOutline },
  info: { Component: Info },
  'lang-selection-flag-en-gb': { Component: LangSelectionGBFlag },
  'lang-selection-flag-en-us': { Component: LangSelectionUSFlag },
  leeds: { Component: Leeds },
  link: { Component: Link },
  location: { Component: Location },
  lock: { Component: Lock },
  'magnifying-glass': { Component: MagnifyingGlass },
  'map-refresh': { Component: MapRefresh },
  'media-file': { Component: MediaFile },
  minus: { Component: Minus },
  overflow: { Component: Overflow },
  'person-circle': { Component: PersonCircle },
  'pin-drop': { Component: PinDrop },
  plus: { Component: Plus },
  share: { Component: Share },
  tourbook: { Component: Tourbook },
  'tourbook-filled': { Component: TourbookFilled },
  'tourbook-with-no-listings': { Component: TourbookWithNoListings },
  trash: { Component: Trash },
  'truva-t': { Component: TruvaT },
  union: { Component: Union },
  upload: { Component: Upload },
  'vts-logo-black': { Component: VtsLogoBlack },
  'vts-logo-indigo': { Component: VtsLogoIndigo },
  wiredScore: { Component: WiredScore },
  wiredScoreCertified: { Component: WiredScoreCertified },
  wiredScoreGold: { Component: WiredScoreGold },
  wiredScorePlatinum: { Component: WiredScorePlatinum },
  wiredScoreSilver: { Component: WiredScoreSilver },
  emptyBuilding: { Component: EmptyBuilding },
} as const;

export type ValidIconTypes = keyof typeof componentMappings;

type Props = {
  type: ValidIconTypes;
  title?: string;
  className?: string;
  onClick?: (e: React.MouseEvent) => void;
};

const CustomIcon = ({ type, ...passProps }: Props) => {
  const flags = useFlags();

  if (componentMappings[type]) {
    return (
      <Icon
        component={componentMappings[type].Component as React.FC<React.SVGProps<SVGSVGElement>>}
        {...passProps}
      />
    );
  }
  if (!flags['remove-legacy-icons']) {
    return <LegacyIcon {...passProps} type={type} />;
  }

  Bugsnag.notify(`Unsupported icon type: ${type}`);
  return null;
};

export default CustomIcon;
